import React, {useState} from 'react';
import type {InjectedRouter} from 'react-router';

import {resource} from '@albert-io/json-api-framework/request/builder';
import {addToast, IconButton, Menu, copyToClipboard} from '@albert-io/atomic';
import {normalizeError} from 'lib/normalizeError';
import ArchivedQuestionModal from 'client/components/ArchivedQuestions/ArchivedQuestionModal.react';
import {getArchivedQuestionCount} from 'client/components/ArchivedQuestions/useArchivedQuestionModal';

import type {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';

import {AssignmentActionModals} from './AssignmentActionModals';
import type {AssignmentModalType} from './AssignmentActionModals';
import type {AssignmentDeleteScope} from './AssignmentMenu.helpers';
import {buildDeleteRequest, getEditPath, getShareUrl} from './AssignmentMenu.helpers';
import {useAssignmentDeleteScope} from './hooks/useAssignmentDeleteScope';

// Menu.Item fires onClick on both click and Enter/Space, so one handler covers keyboard too.
type ActivateHandler = (e?: React.MouseEvent | React.KeyboardEvent) => void;

// "Document Details" shows the creator's name, which the lean GQL row lacks (only teacherId).
// !todo: drop this fetch once BE adds the teacher (creator) name to the Assignment GQL type.
const fetchCreatorName = async (assignmentId: string, isDraft: boolean): Promise<string | null> => {
  const query = resource(isDraft ? 'draft_assignment_v1' : 'assignment_v3')
    .mandarkEndpoint([isDraft ? 'draft_assignments_v1' : 'assignments_v3', assignmentId])
    .include('teacher_v1');
  // Mandark caches a failed interest forever and resolves it as an empty stub — re-dirty so a
  // retry refetches instead of throwing on the stub.
  if (!query.isResourceValid()) {
    query.invalidateInterest();
  }
  const assignment = (await query.getResourcePromise()) as AssignmentModelV3;

  return assignment.getTeacher()?.getFullName() ?? null;
};

interface FolderQuestionInfo {
  questionCount: number;
  archivedQuestionCount: number;
}

// The lean GQL row can't see archived questions, so fetch the assignment's sets to count them — the
// shared HeaderElements menu does this from a Mandark model the GQL list doesn't carry.
const fetchFolderQuestionInfo = async (
  assignmentId: string,
  isDraft: boolean
): Promise<FolderQuestionInfo> => {
  const query = resource(isDraft ? 'draft_assignment_v1' : 'assignment_v3')
    .mandarkEndpoint([isDraft ? 'draft_assignments_v1' : 'assignments_v3', assignmentId])
    .include('question_sets_v1.questions_v3');
  // Same failed-interest re-dirty as fetchCreatorName — otherwise one blip wedges this modal forever.
  if (!query.isResourceValid()) {
    query.invalidateInterest();
  }
  const assignment = (await query.getResourcePromise()) as AssignmentModelV3;

  const questionSets = assignment.getQuestionSets();
  const questionCount = questionSets.reduce((total, set) => total + set.getQuestions().size, 0);

  return {questionCount, archivedQuestionCount: getArchivedQuestionCount(questionSets)};
};

interface AssignmentMenuOptionsProps {
  assignmentId: string;
  assignmentName: string;
  isDraft: boolean;
  /** The acting teacher created the assignment — gates deleting a draft, or the assignment itself. */
  isCreator: boolean;
  /** Class-scoped list → Delete unassigns only this class. */
  classroomId?: string;
  /** Cross-class "View Student Answers" only exists behind the FRQ teacher-grading flag. */
  isFrqGradingEnabled?: boolean;
  /** Report path for this row; absent (e.g. no acting teacher) hides View Report. */
  reportPath?: string | null;
  /** ISO timestamp for the Document Details "Last modified" line. */
  updatedAt: string;
  router: InjectedRouter;
  onDeleteComplete: () => void;
}

export const AssignmentMenuOptions = ({
  assignmentId,
  assignmentName,
  isDraft,
  isCreator,
  classroomId,
  isFrqGradingEnabled,
  reportPath,
  updatedAt,
  router,
  onDeleteComplete
}: AssignmentMenuOptionsProps) => {
  const [modal, setModal] = useState<AssignmentModalType>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  // A fetch can legitimately resolve null (no teacher) — null must not read as "not yet fetched".
  const [creatorFetched, setCreatorFetched] = useState(false);
  const [folderInfo, setFolderInfo] = useState<FolderQuestionInfo | null>(null);
  const [isPreparingFolder, setIsPreparingFolder] = useState(false);
  const {
    data: fetchedDeleteScope,
    isLoading: isPreparingDelete,
    read: readDeleteScope
  } = useAssignmentDeleteScope();

  const isClassScoped = Boolean(classroomId);
  const canViewStudentAnswers = isClassScoped || Boolean(isFrqGradingEnabled);
  // Any teacher of a class on a published assignment can remove their own classes from it. A draft
  // carries no classes, so there is nothing for a non-creator to remove.
  const canDelete = !isDraft || isCreator;
  const editPath = getEditPath(assignmentId, isDraft);

  // A class-scoped delete unassigns the class being viewed and nothing else, and a draft is deleted
  // outright — neither needs to know what else the assignment is assigned to.
  const deleteNeedsRead = !isDraft && !isClassScoped;

  const deleteScope: AssignmentDeleteScope | null = deleteNeedsRead
    ? fetchedDeleteScope && {...fetchedDeleteScope, isCreator}
    : {classroomIds: classroomId ? [classroomId] : [], isCreator, totalClassroomCount: null};

  const openModal =
    (type: AssignmentModalType): ActivateHandler =>
    (e) => {
      e?.stopPropagation();
      setModal(type);
    };

  const goToEdit: ActivateHandler = (e) => {
    e?.stopPropagation();
    router.push(editPath);
  };

  const goToReport: ActivateHandler = (e) => {
    e?.stopPropagation();
    if (reportPath) {
      router.push(reportPath);
    }
  };

  const openStudentAnswers: ActivateHandler = (e) => {
    e?.stopPropagation();
    if (!canViewStudentAnswers) {
      return;
    }
    // The submissions index resolves the first student to show and redirects into their view.
    router.push(
      classroomId
        ? `/classes/${classroomId}/assignments/${assignmentId}/student-detail`
        : `/assignments/${assignmentId}/student-detail`
    );
  };

  const openDetails: ActivateHandler = async (e) => {
    e?.stopPropagation();
    if (isLoadingDetails) {
      return;
    }
    if (!creatorFetched) {
      setIsLoadingDetails(true);
      try {
        setCreatorName(await fetchCreatorName(assignmentId, isDraft));
        setCreatorFetched(true);
      } catch (error) {
        addToast({
          color: 'negative',
          title: "Couldn't load details",
          message: normalizeError(error).message
        });
        return;
      } finally {
        setIsLoadingDetails(false);
      }
    }
    setModal('details');
  };

  const openFolderModal: ActivateHandler = async (e) => {
    e?.stopPropagation();
    if (isPreparingFolder) {
      return;
    }
    setIsPreparingFolder(true);
    try {
      const info = await fetchFolderQuestionInfo(assignmentId, isDraft);
      setFolderInfo(info);
      // Warn first when archived questions exist — they're excluded from what gets saved.
      setModal(info.archivedQuestionCount > 0 ? 'archived' : 'folder');
    } catch (error) {
      addToast({
        color: 'negative',
        title: "Couldn't open Save to Folder",
        message: normalizeError(error).message
      });
    } finally {
      setIsPreparingFolder(false);
    }
  };

  const openDeleteModal: ActivateHandler = async (e) => {
    e?.stopPropagation();
    if (isPreparingDelete) {
      return;
    }
    if (deleteNeedsRead) {
      const scope = await readDeleteScope(assignmentId);
      if (!scope) {
        return;
      }
      // No request to make: the actor teaches none of its classes, so there is nothing of theirs to
      // remove, and the classes that are on it belong to other teachers.
      if (!buildDeleteRequest({assignmentId, isDraft, scope: {...scope, isCreator}})) {
        addToast({
          color: 'info',
          title: 'Nothing to remove',
          message: `${assignmentName} isn't assigned to any of the classes you teach.`
        });
        return;
      }
    }
    setModal('delete');
  };

  const copyShareLink =
    (type: 'student' | 'educator'): ActivateHandler =>
    (e) => {
      e?.stopPropagation();
      copyToClipboard(getShareUrl(type, assignmentId), () =>
        addToast({
          color: 'positive',
          title: 'Link Copied!',
          message: `The ${type} link to this assignment has been copied to your clipboard.`
        })
      );
    };

  // Divider-separated groups of Menu.Items; Menu closes itself on item click (closeOnItemClick).
  const groups: {key: string; items: React.ReactNode[]}[] = [
    {
      key: 'edit',
      items: [
        <Menu.Item
          key='edit'
          leftIconProps={{icon: isDraft ? 'pencil' : 'gear'}}
          onClick={goToEdit}
        >
          {isDraft ? 'Edit Draft' : 'Edit Recipient Settings'}
        </Menu.Item>
      ]
    },
    {
      key: 'reports',
      items: isDraft
        ? []
        : [
            reportPath ? (
              <Menu.Item key='report' leftIconProps={{icon: 'chart-line'}} onClick={goToReport}>
                View Report
              </Menu.Item>
            ) : null,
            // When unavailable (cross-class without the grading flag), keep it non-actionable +
            // aria-disabled but readable per DQA. `readonly` blocks the click like `disabled`
            // without the atomic's faded icon/opacity; we add the not-allowed cursor +
            // content-tertiary color on top.
            <Menu.Item
              key='answers'
              leftIconProps={{
                icon: 'file-lines',
                color: canViewStudentAnswers ? undefined : 'tertiary'
              }}
              textProps={canViewStudentAnswers ? undefined : {color: 'tertiary'}}
              className={
                canViewStudentAnswers ? undefined : 'pointer-events-auto! cursor-not-allowed!'
              }
              aria-disabled={canViewStudentAnswers ? undefined : true}
              disableHover={!canViewStudentAnswers}
              onClick={openStudentAnswers}
              readonly={!canViewStudentAnswers}
              subLabel={
                canViewStudentAnswers ? undefined : 'Enabled when viewing an individual classroom.'
              }
            >
              View Student Answers
            </Menu.Item>
          ]
    },
    {
      key: 'manage',
      items: [
        // Duplicate & Reassign duplicates a published assignment onto classes — not applicable to drafts.
        !isDraft ? (
          <Menu.Item
            key='reassign'
            leftIconProps={{icon: 'rotate-right'}}
            onClick={openModal('reassign')}
          >
            Duplicate & Reassign
          </Menu.Item>
        ) : null,
        // Save Questions to Folder works for drafts too — openFolderModal/fetchFolderQuestionInfo
        // already branch on isDraft (draft_assignments_v1).
        <Menu.Item
          key='folder'
          leftIconProps={{icon: 'folder'}}
          onClick={openFolderModal}
          disabled={isPreparingFolder}
        >
          Save Questions to Folder
        </Menu.Item>
      ]
    },
    {
      key: 'share',
      items: isDraft
        ? []
        : [
            <Menu.Item
              key='student-share'
              leftIconProps={{icon: 'copy'}}
              onClick={copyShareLink('student')}
            >
              Student Share Link
            </Menu.Item>,
            <Menu.Item
              key='educator-share'
              leftIconProps={{icon: 'copy'}}
              onClick={copyShareLink('educator')}
            >
              Educator Share Link
            </Menu.Item>
          ]
    },
    {
      key: 'meta',
      items: [
        <Menu.Item
          key='details'
          leftIconProps={{icon: 'memo-circle-info'}}
          onClick={openDetails}
          disabled={isLoadingDetails}
        >
          Document Details
        </Menu.Item>,
        <Menu.Item
          key='delete'
          leftIconProps={{icon: 'trash-can', color: 'negative'}}
          textProps={{color: 'negative'}}
          onClick={openDeleteModal}
          disabled={!canDelete || isPreparingDelete}
          subLabel={canDelete ? undefined : "You don't have permission to delete this assignment."}
          data-testid={`delete-${assignmentId}`}
        >
          Delete
        </Menu.Item>
      ]
    }
  ];

  const visibleGroups = groups
    .map((group) => ({key: group.key, items: group.items.filter(Boolean)}))
    .filter((group) => group.items.length > 0);

  return (
    <>
      <Menu closeOnItemClick position='bottom-end' positionFixed>
        <Menu.Trigger>
          {(triggerProps) => (
            <IconButton
              {...triggerProps}
              icon='ellipsis-v'
              size='s'
              label={`More options for ${assignmentName}`}
              tooltip='More options'
              data-testid={`actions-${assignmentId}`}
            />
          )}
        </Menu.Trigger>
        <Menu.Content className='min-w-[245px]'>
          {visibleGroups.map((group, index) => (
            <React.Fragment key={group.key}>
              {index > 0 && <Menu.Divider />}
              {group.items}
            </React.Fragment>
          ))}
        </Menu.Content>
      </Menu>

      <ArchivedQuestionModal
        isOpen={modal === 'archived'}
        archivedQuestionCount={folderInfo?.archivedQuestionCount ?? 0}
        type='save-to-folder'
        onConfirm={() => setModal('folder')}
        onCancel={() => setModal(null)}
      />

      <AssignmentActionModals
        modal={modal}
        onClose={() => setModal(null)}
        assignmentId={assignmentId}
        assignmentName={assignmentName}
        isDraft={isDraft}
        deleteScope={deleteScope}
        creatorName={creatorName}
        updatedAt={updatedAt}
        questionCount={folderInfo?.questionCount ?? 0}
        archivedQuestionCount={folderInfo?.archivedQuestionCount ?? 0}
        onDeleted={onDeleteComplete}
      />
    </>
  );
};
